import React, { useState } from "react";
import { STHeroCommon } from "..";
import styles from "./STNewsFeed.module.css";
import clsx from "clsx";
import { newsFeedData, newsFeedTabs } from "@/const/data";
import Image from "next/image";
import { Icons } from "@/const/icons";
import Link from "next/link";
import { useRouter } from "next/router";
import { useBlogs } from "@/hooks";
import { STLoader } from "..";

export const STNewsFeed = () => {
  const router = useRouter();
  const [activeTab, setActiveTab] = useState("Recent News");
  const { blogs, loading, fetchBlogs } = useBlogs();

  React.useEffect(() => {
    // Fetching more to allow for tab filtering on frontend for now
    fetchBlogs(1, 100);
  }, [fetchBlogs]);

  React.useEffect(() => {
    if (router.isReady && router.query.tab) {
      const tabFromQuery = router.query.tab;
      const matchingTab = newsFeedTabs.find(
        (t) => t.toLowerCase() === tabFromQuery.toLowerCase()
      );
      if (matchingTab) {
        setActiveTab(matchingTab);
      }
    }
  }, [router.isReady, router.query.tab]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    router.push(
      {
        pathname: router.pathname,
        query: { ...router.query, tab: tab.toLowerCase() },
      },
      undefined,
      { shallow: true }
    );
  };

  const filteredBlogs = blogs && blogs.length > 0
    ? blogs.filter((b) => {
      const category = b.category?.toLowerCase();
      const tab = activeTab.toLowerCase();

      if (tab === "blogs") return category === "blog" || category === "blogs";
      if (tab === "recent news") return category === "recent news" || category === "news";

      return category === tab;
    })
    : newsFeedData.filter((b) => b.category === activeTab);
  return (
    <>
      <STHeroCommon
        title={activeTab === "News" ? "News" : activeTab}
        description={
          "Follow our latest product updates, certifications, partnerships, and milestones as we scale sustainable materials. From research breakthroughs to real-world adoption, these updates reflect how our technology is creating impact."
        }
      />
      <section className="space-xl-py">
        <div className="container">
          <div
            className={clsx(
              "d-flex gap-md justify-content-between align-items-start space-md-mb",
            )}
          >
            {/* Tabs */}
            <div
              className={clsx(
                "d-flex overflow-x-auto flex-nowrap",
                styles.tabs,
              )}
            >
              {newsFeedTabs.map((tab) => (
                <button
                  key={tab}
                  onClick={() => handleTabChange(tab)}
                  className={clsx(
                    "h6 mb-0 fw-normal cursor-pointer transition-smooth",
                    styles.tab,
                    activeTab === tab && styles.activeTab,
                  )}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          {/* Cards */}
          {loading ? (
            <div className="d-flex justify-content-center space-xl-py">
              <STLoader />
            </div>
          ) : filteredBlogs.length > 0 ? (
            <div className={clsx("gap-sm", styles.newsCardWrapper)}>
              {filteredBlogs.map((item) => (
                <Link
                  key={item.id}
                  href={`/news-feed/${item.slug}/`}
                  className={clsx(
                    "bg-off-white space-xs-p radius-md d-block text-decoration-none",
                    styles.newsCard,
                  )}
                >
                  <div
                    className={clsx(
                      "ratio radius-sm overflow-hidden",
                      styles.newsImageWrap,
                    )}
                  >
                    {item.image ? (
                      <Image
                        src={item.image}
                        alt={item.name || item.title}
                        fill
                        className="transition-smooth object-fit-contain bg-white"
                      />
                    ) : (
                      <div className="bg-secondary w-100 h-100 d-flex align-items-center justify-content-center text-white">
                        No Image
                      </div>
                    )}
                  </div>

                  <p
                    className={clsx(
                      "secondary-title mb-0 space-xs-pt h6",
                      styles.newsTitle,
                    )}
                  >
                    {item.name || item.title}
                  </p>
                </Link>
              ))}
            </div>
          ) : (
            <div className={clsx(styles.emptyState, "text-center radius-lg bg-off-white")}>
              <div className={styles.emptyIcon}>{Icons.newsIcon}</div>
              <h3 className="secondary-title space-xs-mt mb-0">
                No {activeTab.toLowerCase()} found
              </h3>
              <p className="paragraph-text mb-0 opacity-75">
                We haven&apos;t posted any {activeTab.toLowerCase()} yet. <br />
                Please check back later for updates!
              </p>
            </div>
          )}
        </div>
      </section>
    </>
  );
};
